import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Save, Check, CircleCheck, CircleMinus, Edit2, Lock } from 'lucide-react';
import Skeleton from '../common/Skeleton';
import { governanceFrameworksService, governanceApplicableLawsService, GovernanceFramework, ApplicableLaw } from '../../services/governanceService';
import GovernancePoliciesModal from './GovernancePoliciesModal';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmWarning, confirmActive } from '../common/confirm';
import NoDataFound from '../common/NoDataFound';
import StatusSwitch from '../common/StatusSwitch';
import ApprovalDot, { WorkflowState } from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import { usePermissions, usePermission } from '../../contexts/PermissionContext';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea, ValidatedDatePicker } from '../../validation/ValidatedFields';
import { isUniqueLocal } from '../../validation/uniqueness';
import { useFormatDate } from '../../contexts/EntitySettingsContext';

const ACT_FRAMEWORKS = 'GOV_FRAMEWORKS';
const WF_KIND = 'gov_framework';

interface GovernanceFrameworksProps {
  onViewPolicies?: (framework: GovernanceFramework) => void;
}


const GovernanceFrameworks: React.FC<GovernanceFrameworksProps> = ({ onViewPolicies }) => {
  const [frameworks, setFrameworks] = useState<GovernanceFramework[]>([]);
  const [applicableLaws, setApplicableLaws] = useState<ApplicableLaw[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
  const [selectedFramework, setSelectedFramework] = useState<GovernanceFramework | null>(null);
  const [workflowRow, setWorkflowRow] = useState<GovernanceFramework | null>(null);
  const { check } = usePermissions();
  const { allowed: canView, loading: viewLoading } = usePermission(ACT_FRAMEWORKS, 'view');
  const canModify = check(ACT_FRAMEWORKS, 'view+modify').allowed;
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<Partial<GovernanceFramework>>({});
  const [showPoliciesModal, setShowPoliciesModal] = useState(false);
  const [policiesFramework, setPoliciesFramework] = useState<GovernanceFramework | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [isFieldEditMode, setIsFieldEditMode] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const fieldVh = useFieldValidation();
  const formatDate = useFormatDate();

  useEffect(() => {
    setSubmitting(false);
    setToggling(false);
    setIsFieldEditMode(false);
  }, [selectedFramework, showModal]);

  useEffect(() => {
    governanceApplicableLawsService.getDropdown()
      .then(setApplicableLaws)
      .catch(() => {});
  }, []);

  const loadFrameworks = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const data = await governanceFrameworksService.getAll({
        status: statusFilter as any,
        search: searchTerm || undefined,
        limit: 100,
      });
      const mapped = data.map((fw: any) => ({
        ...fw,
        framework_description: fw.description || fw.framework_description || '',
        start_date: fw.applicability_date || fw.start_date || '',
        end_date: fw.end_date || '',
        is_active: typeof fw.is_active === 'boolean' ? (fw.is_active ? 1 : 0) : (fw.is_active ?? 0),
        display_order: fw.display_order || 999,
      }));
      setFrameworks(mapped);
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to load governance frameworks' });
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, canView]);

  useEffect(() => {
    loadFrameworks();
  }, [loadFrameworks]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, pageSize]);

  const handleRowClick = (framework: GovernanceFramework) => {
    setPoliciesFramework(framework);
    setShowPoliciesModal(true);
  };

  const handleAddNew = () => {
    setSelectedFramework(null);
    setFormData({
      framework_name: '',
      framework_description: '',
      applicable_law_id: undefined,
      is_global: false,
      is_active: 1,
      start_date: new Date().toISOString().split('T')[0],
    });
    setFieldErrors({});
    fieldVh.reset();
    setIsEditMode(false);
    setShowModal(true);
  };

  const validateForm = (): boolean => {
    // Engine-level format validation (109 name / 110 description / 305 date).
    const formatOk = fieldVh.validateAll([
      { name: 'framework_name', code: 109, value: formData.framework_name || '', required: true },
      { name: 'framework_description', code: 110, value: formData.framework_description || '' },
      { name: 'start_date', code: 305, value: formData.start_date || '', required: true },
    ]);

    // Local uniqueness pre-check on framework_name (code 946, entity-scoped).
    let uniqueOk = true;
    if (!isUniqueLocal(946, formData.framework_name || '', frameworks, selectedFramework?.id)) {
      fieldVh.setError('framework_name', 'Framework with this name already exists');
      uniqueOk = false;
    }

    // Applicable-law required check stays on the legacy field-error path.
    const errors: Record<string, string> = {};
    if (!isEditMode && !formData.applicable_law_id) errors.applicable_law_id = 'Applicable law is required';
    setFieldErrors(errors);

    return formatOk && uniqueOk && Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      setToast({ type: 'error', message: 'Please fix the highlighted fields' });
      return;
    }
    setSubmitting(true);
    try {
      const payload: any = {
        ...formData,
        applicability_date: formData.start_date || undefined,
        description: formData.framework_description || (formData as any).description || undefined,
        framework_type: 'Statute',
      };
      delete payload.start_date;
      delete payload.display_order;
      delete payload.end_date;
      delete payload.framework_description;
      delete payload.framework_code;

      if (isEditMode && selectedFramework) {
        delete payload.is_active;
        await governanceFrameworksService.update(selectedFramework.id, payload);
        setToast({ type: 'success', message: 'Framework updated successfully' });
        setIsFieldEditMode(false);
      } else {
        await governanceFrameworksService.create(payload);
        setToast({ type: 'success', message: 'Framework created successfully' });
      }
      setShowModal(false);
      loadFrameworks();
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to save framework' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async () => {
    if (!selectedFramework) return;
    const next = selectedFramework.is_active ? 0 : 1;
    const confirmFn = selectedFramework.is_active ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? 'Reactivate this framework?' : 'Inactivate this framework?',
      text: next
        ? 'It will appear in active framework lists again.'
        : 'It will be hidden from active framework lists.',
      confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
    });
    if (!ok) return;
    setToggling(true);
    try {
      await governanceFrameworksService.update(selectedFramework.id, { is_active: next });
      const updated = { ...selectedFramework, is_active: next };
      setSelectedFramework(updated);
      setFormData(prev => ({ ...prev, is_active: next }));
      setToast({ type: next ? 'success' : 'warning', message: next ? 'Reactivated.' : 'Inactivated.' });
      loadFrameworks();
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to update status' });
    } finally {
      setToggling(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (name === 'is_active' ? (checked ? 1 : 0) : checked) : value,
    }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const clearFieldError = (name: string) => {
    if (!fieldErrors[name]) return;
    setFieldErrors(prev => {
      const { [name]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [name]: name === 'is_active' ? (checked ? 1 : 0) : checked,
    }));
  };

  const totalRecords = frameworks.length;
  const paginatedFrameworks = frameworks.slice((page - 1) * pageSize, page * pageSize);

  if (!viewLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to reg frameworks." />
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full">
      <div className="netbody-header">
        {/* Status — iOS-style on/off switch (on = active) */}
        <StatusSwitch
          value={statusFilter === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE'}
          onChange={next => setStatusFilter(next)}
          noun="frameworks"
          testId="SEL_framework_status_filter"
        />
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.Input
            type="text"
            placeholder="Search reg frameworks…"
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
            data-testid="INP_framework_search"
          />
          {canModify && (
            <Button variant="fill" icon={Plus} onClick={handleAddNew} data-testid="BTN_framework_create">Create </Button>
          )}
        </div>
      </div>

      <div className="netbody-content">

        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list">
            {!loading && frameworks.length === 0 ? (
              <NoDataFound title="No Reg Frameworks Found" message='Click "Create Reg Framework" to add one.' />
            ) : (
              <div className="table-list-scroll">
                <table className="table-list-table">
                  <thead>
                    <tr>
                      <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                      <th className="table-list-th table-list-cell--sno">S.No</th>
                      <th className="table-list-th">Code</th>
                      <th className="table-list-th">Framework Name</th>
                      <th className="table-list-th">Type</th>
                      <th className="table-list-th">Start Date</th>
                      <th className="table-list-th">Is Global</th>
                    </tr>
                  </thead>
                  <tbody aria-busy={loading} aria-live="polite">
                    {loading && <Skeleton.TableRows cols={['dot', '3xs', 'xs', 'md', 'pill', '2xs', '2xs']} />}
                    {!loading && paginatedFrameworks.map((framework, idx) => (
                      <tr
                        key={framework.id}
                        className={`table-list-row${framework.is_active ? '' : ' is-inactive'}`}
                        onClick={() => handleRowClick(framework)}
                        data-testid={`BTN_framework_row_${framework.id}_open`}
                      >
                        <td
                          className="table-list-cell table-list-cell--dot"
                          onClick={(e) => { e.stopPropagation(); setWorkflowRow(framework); }}
                          title="Workflow — view timeline & act"
                          data-testid={`BTN_framework_row_${framework.id}_workflow`}
                        >
                          <ApprovalDot
                            state={(((framework as any).workflow_state) || 'draft') as WorkflowState}
                            onClick={() => setWorkflowRow(framework)}
                            title="Workflow — view timeline & act"
                          />
                        </td>
                        <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                        <td className="table-list-cell">
                          {framework.code
                            ? <span className="cell-code">{framework.code}</span>
                            : <span className="text-muted">—</span>}
                        </td>
                        <td className="table-list-cell">
                          <span className="table-list-cell-title cell-name-lock">
                            {(framework as any).workflow_state === 'approved' && (
                              <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                            )}
                            {framework.framework_name}
                          </span>
                        </td>
                        <td className="table-list-cell">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                            {framework.framework_type}
                          </span>
                        </td>
                        <td className="table-list-cell">
                          {formatDate(framework.start_date)}
                        </td>
                        <td className="table-list-cell">
                          {framework.is_global
                            ? <span className="status-badge sm info">Global</span>
                            : <span className="status-badge sm neutral">Entity</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              recordLabel="reg frameworks"
            />
          </div>
        </div>
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="md">
        <Modal.Header>
          {isEditMode && selectedFramework ? (
            <>Editing "<span className="item-highlight">{selectedFramework.framework_name}</span>"</>
          ) : (
            'Create'
          )}
        </Modal.Header>
        <Modal.Body>
          <form
            id="framework-form"
            onSubmit={handleSubmit}
            style={{
              display: 'grid',
              gridTemplateColumns: isEditMode ? '1fr 1fr 1fr' : '1fr 1fr',
              columnGap: '1.5rem',
              rowGap: '1rem',
              width: '100%',
              boxSizing: 'border-box'
            }}
            data-testid="MOD_framework_form"
          >
            {isEditMode && (
              <div style={{ minWidth: 0 }}>
                <FormControl.Input
                  label="Code"
                  value={selectedFramework?.code || '—'}
                  disabled
                  data-testid="INP_framework_code"
                />
              </div>
            )}
            <div style={{ minWidth: 0, gridColumn: !isEditMode ? 'span 2' : 'auto' }}>
              <ValidatedInput
                vh={fieldVh}
                code={109}
                required
                label="Framework Name"
                name="framework_name"
                value={formData.framework_name || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, framework_name: e.target.value }))}
                onAfterBlur={(v) => {
                  if (!isUniqueLocal(946, v, frameworks, selectedFramework?.id)) {
                    fieldVh.setError('framework_name', 'Framework with this name already exists');
                  }
                }}
                placeholder="e.g., GDPR, ISO 27001"
                disabled={isEditMode && !isFieldEditMode}
                data-testid="INP_framework_name"
              />
            </div>
            {!isEditMode && (
              <div style={{ minWidth: 0 }}>
                <FormControl.SearchSelect
                  label="Applicable Law"
                  required={!isEditMode}
                  placeholder="Select applicable law…"
                  options={applicableLaws.map(l => ({
                    value: l.id,
                    label: l.jurisdiction ? `${l.law_name} (${l.jurisdiction})` : l.law_name,
                  }))}
                  value={formData.applicable_law_id ?? null}
                  onChange={(value) => {
                    setFormData(prev => ({ ...prev, applicable_law_id: value != null ? Number(value) : undefined }));
                    clearFieldError('applicable_law_id');
                  }}
                  disabled={isEditMode && !isFieldEditMode}
                  error={fieldErrors.applicable_law_id}
                  data-testid="SEL_framework_applicable_law"
                />
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <ValidatedDatePicker
                vh={fieldVh}
                name="start_date"
                code={305}
                required
                label="Effective From Date"
                value={formData.start_date || ''}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, start_date: e.target.value }));
                }}
                disabled={isEditMode && !isFieldEditMode}
                data-testid="INP_framework_start_date"
              />
            </div>
            <div style={{ gridColumn: isEditMode ? 'span 3' : 'span 2' }}>
              <ValidatedTextarea
                vh={fieldVh}
                code={110}
                label="Description"
                name="framework_description"
                value={formData.framework_description || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, framework_description: e.target.value }))}
                rows={3}
                placeholder="Brief description of the framework"
                disabled={isEditMode && !isFieldEditMode}
                data-testid="INP_framework_description"
              />
            </div>
            <FormControl.Checkbox
              label="Is Global Framework"
              checked={formData.is_global || false}
              onChange={(checked) => handleCheckboxChange('is_global', checked)}
              disabled={isEditMode && !isFieldEditMode}
              data-testid="CHK_framework_is_global"
            />
          </form>
        </Modal.Body>
        {isEditMode ? (
          <Modal.Footer className="app-modal-footer--split">
            <Button
              variant={selectedFramework?.is_active ? 'inactive-outline' : 'active-outline'}
              icon={selectedFramework?.is_active ? CircleMinus : CircleCheck}
              onClick={handleToggleActive}
              disabled={toggling || submitting}
              data-testid="BTN_framework_toggle_active"
            >
              {toggling ? '...' : selectedFramework?.is_active ? 'Inactive' : 'Active'}
            </Button>
            {isFieldEditMode ? (
              <Button
                type="submit"
                form="framework-form"
                variant="update"
                icon={Check}
                disabled={submitting || toggling}
                data-testid="BTN_framework_update"
              >
                {submitting ? 'Updating...' : 'Update'}
              </Button>
            ) : (
              <Button
                variant="fill"
                icon={Edit2}
                onClick={() => setIsFieldEditMode(true)}
                data-testid="BTN_framework_edit"
              >
                Edit
              </Button>
            )}
          </Modal.Footer>
        ) : (
          <Modal.Footer>
            <Button
              type="submit"
              form="framework-form"
              variant="fill"
              icon={Save}
              disabled={submitting}
              data-testid="BTN_framework_submit"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {showPoliciesModal && policiesFramework && (
        <GovernancePoliciesModal
          frameworkId={policiesFramework.id}
          frameworkName={policiesFramework.framework_name}
          framework={policiesFramework}
          onClose={() => setShowPoliciesModal(false)}
          onFrameworkUpdated={loadFrameworks}
        />
      )}

      <WorkflowModal
        isOpen={!!workflowRow}
        onClose={() => setWorkflowRow(null)}
        kind={WF_KIND}
        id={workflowRow?.id ?? null}
        onChanged={loadFrameworks}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default GovernanceFrameworks;
